/**
 * ============================================================================
 * COLOR HELPERS - Color Processing & Conversion Utilities
 * ============================================================================
 * 
 * PURPOSE:
 * Provides color manipulation, conversion, and analysis functions for
 * wedding theme generation. Handles hex/RGB/HSL conversions, color naming,
 * contrast calculations, and color parsing.
 * 
 * KEY FUNCTIONS:
 * 
 * 1. getColorName(hex)
 *    - Fetches human-readable color name from hex code
 *    - Uses TheColorAPI with Color Pizza API fallback
 *    - Example: #FF69B4 → "Hot Pink"
 * 
 * 2. hexToRgb(hex)
 *    - Converts hex color to RGB object
 *    - Example: #FF69B4 → { r: 255, g: 105, b: 180 }
 * 
 * 3. rgbToHsl(r, g, b)
 *    - Converts RGB to HSL for color analysis
 *    - Used for determining color brightness/saturation
 * 
 * 4. getContrastColor(hex)
 *    - Returns black or white for optimal text contrast
 *    - Used for text on colored backgrounds
 * 
 * 5. interpolateRgb(color1, color2, factor)
 *    - Blends two colors for gradient effects
 *    - Used in color flow animations
 * 
 * 6. parseColors(colorData)
 *    - Parses color data from backend responses
 *    - Handles various color formats
 * 
 * 7. extractHexFromColor(colorObj)
 *    - Extracts hex code from color object
 *    - Handles multiple color object formats
 * 
 * USAGE:
 * - Used by all theme rendering components
 * - Color palette displays
 * - AI Color Magic modal
 * - Theme comparison views
 * 
 * EXTERNAL APIS:
 * - TheColorAPI (primary): https://www.thecolorapi.com
 * - Color Pizza API (fallback): https://api.color.pizza
 * 
 * ============================================================================
 */

// Color Processing Helper Functions
// Extracted from Dream Wedding Generator and Moodboard Matcher

/**
 * Get human-readable color name from hex code using external APIs
 * 
 * @param {String} hex - Hex color code (with or without #)
 * @returns {Promise<String>} Human-readable color name
 */
export const getColorName = async (hex) => {
  try {
    const cleanHex = hex.replace('#', '');
    const response = await fetch(`https://www.thecolorapi.com/id?hex=${cleanHex}`);
    const data = await response.json();
    return data.name.value;
  } catch (error) {
    console.log('TheColorAPI failed, trying Color Pizza API:', error);
    // Fallback to Color Pizza API
    try {
      const cleanHex = hex.replace('#', '');
      const response = await fetch(`https://api.color.pizza/v1/${cleanHex}`);
      const data = await response.json();
      return data.colors[0].name;
    } catch (fallbackError) {
      console.log('Both color APIs failed:', fallbackError);
      return 'Custom Color';
    }
  }
};

// ============================================================================
// COLOR EXTRACTION HELPERS
// ============================================================================

/**
 * Extract hex code from "Name (#HEX)" format
 * 
 * HANDLES FORMATS:
 * - "#FFB6C1" → "#FFB6C1"
 * - "Blush Pink (#FFB6C1)" → "#FFB6C1"
 * - "Invalid" → "#E5E7EB" (gray fallback)
 * 
 * USAGE:
 * - Theme rendering: Extract hex from backend color format
 * - Color modal: Get hex for AI Color Magic
 * 
 * @param {String} colorString - Color in various formats
 * @returns {String} Hex color code
 */
export const extractHexFromColor = (colorString) => {
  // Validation: return fallback if no color provided
  if (!colorString) return '#E5E7EB';
  
  // Already a hex code - return as is
  if (colorString.startsWith('#')) {
    return colorString;
  }
  
  // Extract hex from "Name (#HEX)" format using regex
  const hexMatch = colorString.match(/#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})/);
  return hexMatch ? hexMatch[0] : '#E5E7EB';  // Return hex or fallback
};

/**
 * Extract color name from "Name (#HEX)" format with API fallback
 * 
 * HANDLES FORMATS:
 * - "Blush Pink (#FFB6C1)" → "Blush Pink"
 * - "#FFB6C1" → Fetches name from API → "Hot Pink"
 * - "Invalid" → "Unknown Color"
 * 
 * API FALLBACK:
 * - If only hex provided, calls getColorName() to fetch human-readable name
 * - Uses TheColorAPI with Color Pizza API fallback
 * 
 * @param {String} colorString - Color in various formats
 * @returns {Promise<String>} Human-readable color name
 */
export const extractNameFromColor = async (colorString) => {
  // Validation: return fallback if no color provided
  if (!colorString) return 'Unknown Color';
  
  // Extract name from "Name (#HEX)" format
  if (colorString.includes('(#')) {
    const nameMatch = colorString.match(/^(.+?)\s*\(#/);
    if (nameMatch) {
      return nameMatch[1].trim();  // Return extracted name
    }
  }
  
  // If only hex code provided, fetch name from API
  if (colorString.startsWith('#')) {
    try {
      return await getColorName(colorString);
    } catch (error) {
      return 'Custom Color';  // Fallback if API fails
    }
  }
  
  // Return as is if already a name
  return colorString;
};

// ============================================================================
// COLOR PARSING HELPERS
// ============================================================================

/**
 * Parse colors (handles both arrays and comma-separated strings)
 * 
 * HANDLES FORMATS:
 * - Array: ['#FFB6C1', '#FFD700'] → ['#FFB6C1', '#FFD700']
 * - String: '#FFB6C1, #FFD700' → ['#FFB6C1', '#FFD700']
 * - Invalid: null → []
 * 
 * USAGE:
 * - Backend responses may return colors as string or array
 * - Normalizes to array for consistent processing
 * 
 * @param {Array|String} colors - Colors in various formats
 * @returns {Array} Array of color strings
 */
export const parseColors = (colors) => {
  // Validation: return empty array if no colors
  if (!colors) return [];
  
  // Already an array - return as is
  if (Array.isArray(colors)) {
    return colors;
  }
  
  // Parse comma-separated string
  if (typeof colors === 'string') {
    return colors
      .split(',')                    // Split by comma
      .map(color => color.trim())    // Trim whitespace
      .filter(color => color.length > 0);  // Remove empty strings
  }
  
  // Unknown format - return empty array
  return [];
};

// ============================================================================
// COLOR CONTRAST HELPER
// ============================================================================

/**
 * Get contrast color for text readability
 * 
 * ALGORITHM:
 * 1. Convert hex to RGB
 * 2. Calculate brightness using luminance formula
 * 3. Return black for bright backgrounds, white for dark backgrounds
 * 
 * LUMINANCE FORMULA:
 * brightness = (R * 299 + G * 587 + B * 114) / 1000
 * - Weights account for human eye sensitivity to different colors
 * - Green contributes most to perceived brightness
 * 
 * USAGE:
 * - Color cards: Ensure text is readable on colored backgrounds
 * - Buttons: Ensure button text contrasts with button color
 * 
 * @param {String} hexColor - Background hex color
 * @returns {String} '#000000' (black) or '#FFFFFF' (white)
 */
export const getContrastColor = (hexColor) => {
  // Validation: return black if no color provided
  if (!hexColor) return '#000000';
  
  // Convert hex to RGB
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Calculate brightness (0-255 scale)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  
  // Return black for bright backgrounds (>128), white for dark backgrounds
  return brightness > 128 ? '#000000' : '#FFFFFF';
};

// ============================================================================
// COLOR CONVERSION HELPERS
// ============================================================================

/**
 * Convert hex to RGB object
 * 
 * @param {String} hex - Hex color code (e.g., '#FFB6C1')
 * @returns {Object|null} { r, g, b } values (0-255) or null if invalid
 */
export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

/**
 * Interpolate between two RGB colors
 * 
 * USAGE:
 * - Color flow animations: Create smooth gradients
 * - Color transitions: Blend between colors
 * 
 * @param {String} color1 - Start hex color
 * @param {String} color2 - End hex color
 * @param {Number} factor - Interpolation factor (0-1)
 * @returns {Object} Interpolated RGB { r, g, b }
 */
export const interpolateRgb = (color1, color2, factor) => {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);
  
  if (!rgb1 || !rgb2) return color1;
  
  const r = Math.round(rgb1.r + factor * (rgb2.r - rgb1.r));
  const g = Math.round(rgb1.g + factor * (rgb2.g - rgb1.g));
  const b = Math.round(rgb1.b + factor * (rgb2.b - rgb1.b));
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

/**
 * Convert RGB to HSL (Hue, Saturation, Lightness)
 * 
 * HSL COLOR MODEL:
 * - Hue (H): Color type (0-360 degrees on color wheel)
 * - Saturation (S): Color intensity (0-1, 0=gray, 1=vivid)
 * - Lightness (L): Brightness (0-1, 0=black, 0.5=pure color, 1=white)
 * 
 * USAGE:
 * - Color analysis: Determine if color is bright/dark, vivid/muted
 * - Color sorting: Sort by hue, saturation, or lightness
 * - Color adjustments: Lighten/darken colors programmatically
 * 
 * @param {Number} r - Red value (0-255)
 * @param {Number} g - Green value (0-255)
 * @param {Number} b - Blue value (0-255)
 * @returns {Object} { h, s, l } where h is 0-1, s is 0-1, l is 0-1
 */
export const rgbToHsl = (r, g, b) => {
  // Normalize RGB values to 0-1 range
  r /= 255;
  g /= 255;
  b /= 255;
  
  // Find max and min values for lightness calculation
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;  // Lightness is average of max and min
  
  // Check if color is achromatic (grayscale)
  if (max === min) {
    h = s = 0; // achromatic (no hue, no saturation)
  } else {
    // Calculate saturation
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    // Calculate hue based on which RGB component is dominant
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;  // Red is dominant
      case g: h = (b - r) / d + 2; break;                // Green is dominant
      case b: h = (r - g) / d + 4; break;                // Blue is dominant
    }
    h /= 6;  // Normalize hue to 0-1 range
  }
  
  return { 
    h: Math.round(h * 360), 
    s: Math.round(s * 100), 
    l: Math.round(l * 100) 
  };
};
